<script setup>
// Коментарі під плеєром — власна гілка (той самий ChatBody, що й у чаті):
// пишуть, лайкають і відповідають прямо тут, увійшовши через Twitch чи
// Discord. Старі коментарі з comments.app перенесені в ту саму гілку.

const props = defineProps({
  // Ідентифікатор гілки коментарів — має лишатися незмінним у часі
  pageId: {
    type: String,
    required: true
  },
  // Заголовок блока на сторінці
  title: {
    type: String,
    default: ''
  },
  // Назва і адреса, які comments.app показує в панелі модерації та в
  // сповіщеннях телеграм-бота
  pageTitle: {
    type: String,
    default: ''
  },
  pageUrl: {
    type: String,
    default: ''
  },
  // Параметри для аналітики: слуг аніме і номер серії (для блока на все
  // аніме серії немає)
  anime: {
    type: String,
    default: ''
  },
  episode: {
    type: Number,
    default: 0
  }
})

// Блок на сторінці оновлюється рідше за вікно чату: їх на сторінці двоє, а
// дивляться в них не так пильно
const POLL_INTERVAL = 15000

const rootRef = ref(null)

// Список підтягуємо тільки коли блок доїхав до вʼюпорта — сторінка серії і без
// нього тягне достатньо. Далі слухачі одразу знімаються.
const PRELOAD_MARGIN = 300
const activated = ref(false)
const stopListeners = []

const checkVisible = () => {
  const el = rootRef.value
  if (!el || activated.value) return

  const rect = el.getBoundingClientRect()
  if (
    rect.top > window.innerHeight + PRELOAD_MARGIN ||
    rect.bottom < -PRELOAD_MARGIN
  ) {
    return
  }

  activated.value = true
  stopListeners.forEach((stop) => stop())
  trackOnce('view', 'comments_view')
}

onMounted(() => {
  stopListeners.push(
    useEventListener(window, 'scroll', checkVisible, { passive: true }),
    useEventListener(window, 'resize', checkVisible, { passive: true })
  )
  checkVisible()
})

// --- Аналітика (Zaraz -> Google Analytics) ---
// Події шлемо не частіше одного разу на гілку: цікаво, скільки людей дійшло
// до коментарів, а не скільки разів вони клікнули
const tracked = reactive({ view: false, click: false })

const track = (event) => {
  if (!import.meta.client || !window.zaraz) return

  window?.zaraz?.track(event, {
    anime: props.anime || props.pageId,
    episode: props.episode || null,
    block: props.episode ? 'episode' : 'anime'
  })
}

const trackOnce = (key, event) => {
  if (tracked[key]) return
  tracked[key] = true
  track(event)
}

// Нова гілка — рахуємо перегляд і клік заново
watch(
  () => props.pageId,
  () => {
    tracked.view = false
    tracked.click = false
    if (activated.value) trackOnce('view', 'comments_view')
  }
)
</script>

<template>
  <div
    ref="rootRef"
    class="mt-[10px]"
  >
    <div class="font-bold text-2xl">{{ title || $t('comments') }}</div>

    <!-- Висота за вмістом, але не вище за стелю: довга гілка прокручується
         всередині блока, а поле написання лишається внизу на виду.
         Мінімальна висота — під підказки смайлів над полем: у порожній
         гілці блок інакше обрізав би їх -->
    <div
      class="flex flex-col bg-[hsla(var(--b1)/1)] mt-[10px] rounded-[12px] min-h-[260px] max-h-[520px] overflow-hidden"
      @click="trackOnce('click', 'comments_click')"
    >
      <div
        v-if="!activated"
        class="w-full h-[120px] animate-pulse"
      />

      <ChatBody
        v-else
        :page-id="pageId"
        :page-title="pageTitle"
        :page-url="pageUrl"
        :interval="POLL_INTERVAL"
        empty-text="Коментарів ще немає — напишіть перший"
        variant="page"
      />
    </div>
  </div>
</template>
